import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminFetch, formatBytes, type ClientStats } from '@/lib/api';
import { useState } from 'react';

export function ClientsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => adminFetch<{ clients: ClientStats[] }>('/clients'),
  });
  const { data: profilesData } = useQuery({
    queryKey: ['compliance-profiles'],
    queryFn: () =>
      adminFetch<{ profiles: Array<{ country_code: string; name: string }> }>(
        '/sales/compliance-profiles',
      ),
  });

  const updateModules = useMutation({
    mutationFn: (payload: {
      clientId: number;
      animal_sales: boolean;
      sales_compliance_country?: string | null;
    }) =>
      adminFetch(`/clients/${payload.clientId}/modules`, {
        method: 'PATCH',
        body: JSON.stringify({
          animal_sales: payload.animal_sales,
          sales_compliance_country: payload.sales_compliance_country,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-clients'] }),
  });

  if (isLoading) return <p className="text-neutral-400">Carregando clientes…</p>;
  if (error) return <p className="text-red-400">{error instanceof Error ? error.message : 'Erro'}</p>;

  const clients = data?.clients ?? [];
  const profiles = profilesData?.profiles ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Clientes</h1>
          <p className="text-sm text-neutral-400">
            Uso de mídia ML e módulos opcionais (vendas por cotas)
          </p>
        </div>
        <Link
          to="/compliance"
          className="rounded-lg border border-neutral-700 px-3 py-1.5 text-sm text-sky-300 hover:bg-neutral-900"
        >
          Compliance por país
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-800">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Plano</th>
              <th className="px-4 py-3 font-medium">Animais</th>
              <th className="px-4 py-3 font-medium">Storage ML</th>
              <th className="px-4 py-3 font-medium">Vendas cotas</th>
              <th className="px-4 py-3 font-medium">País compliance</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <ClientRow
                key={client.id}
                client={client}
                profiles={profiles}
                busy={updateModules.isPending}
                onToggle={(enabled, country) =>
                  updateModules.mutate({
                    clientId: client.id,
                    animal_sales: enabled,
                    sales_compliance_country: country,
                  })
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClientRow({
  client,
  profiles,
  busy,
  onToggle,
}: {
  client: ClientStats;
  profiles: Array<{ country_code: string; name: string }>;
  busy: boolean;
  onToggle: (enabled: boolean, country: string | null) => void;
}) {
  const enabled = Boolean(client.modules?.animal_sales);
  const [country, setCountry] = useState(client.sales_compliance_country ?? 'BR');

  return (
    <tr className="border-t border-neutral-800">
      <td className="px-4 py-3">
        <div className="font-medium">{client.trade_name || client.legal_name}</div>
        <div className="text-xs text-neutral-500">#{client.id}</div>
      </td>
      <td className="px-4 py-3">{client.plan_code ?? '—'}</td>
      <td className="px-4 py-3">{client.animals_active}</td>
      <td className="px-4 py-3">{formatBytes(client.ml_storage_bytes)}</td>
      <td className="px-4 py-3">
        <label className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={enabled}
            disabled={busy}
            onChange={(e) => onToggle(e.target.checked, country)}
          />
          {enabled ? 'Ativo' : 'Off'}
        </label>
      </td>
      <td className="px-4 py-3">
        <select
          className="rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs"
          value={country}
          disabled={busy}
          onChange={(e) => {
            setCountry(e.target.value);
            onToggle(enabled, e.target.value);
          }}
        >
          {profiles.map((p) => (
            <option key={p.country_code} value={p.country_code}>
              {p.country_code} — {p.name}
            </option>
          ))}
          {profiles.length === 0 && (
            <>
              <option value="BR">BR</option>
              <option value="US">US</option>
            </>
          )}
        </select>
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/media?client_id=${client.id}`}
          className="text-xs font-medium text-sky-400 hover:text-sky-300"
        >
          Ver galeria
        </Link>
      </td>
    </tr>
  );
}
